/**
 * admin/src/routes/ProtectedPlatformAdminRoute.js
 *
 * Guards the entire admin panel. Only users with role === "platform_admin"
 * may pass. All others are redirected to the login page.
 *
 * Force-refreshes the ID token on mount so a revoked platform_admin
 * role is reflected immediately within the same session (closes the UI
 * window where a stale cached claim could still show the panel).
 * The primary enforcement remains the backend verifyPlatformAdmin middleware.
 */
import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@urbamart/shared";
import theme from "@urbamart/shared/theme";

function PageLoader() {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      height: "100dvh", background: theme.colors.bg,
    }}>
      <div className="skeleton" style={{ width: 48, height: 48, borderRadius: "50%" }} />
    </div>
  );
}

export default function ProtectedPlatformAdminRoute({ children }) {
  const { user, loading, isPlatformAdmin, refreshToken } = useAuth();
  const [refreshing, setRefreshing] = useState(true);
  const refreshedUserIdRef = useRef(null);

  useEffect(() => {
    const userId = user?.uid;
    if (!userId) {
      refreshedUserIdRef.current = null;
      setRefreshing(false);
      return;
    }

    if (refreshedUserIdRef.current === userId) {
      setRefreshing(false);
      return;
    }

    refreshedUserIdRef.current = userId;
    setRefreshing(true);
    refreshToken()
      .catch((err) => console.error("ProtectedPlatformAdminRoute: token refresh failed", err))
      .finally(() => setRefreshing(false));
  }, [user, refreshToken]); // PROD-AUDIT FIX (Issue 5): Refresh once per signed-in user to avoid token-refresh loops

  if (loading || refreshing) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isPlatformAdmin) return <Navigate to="/login" replace />;
  return children;
}
